import { yupResolver } from '@hookform/resolvers/yup';
import { memo, useCallback, useId } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { type InferType, object, string } from 'yup';

import { type AirActionTrackingLocation } from '@air/analytics';
import { Agents } from '@air/api';
import { type Board, type Clip } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { Field, FieldLabel, FieldText } from '@air/primitive-field';
import { Input } from '@air/primitive-input';
import { Label } from '@air/primitive-label';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { PrivateBoardSearch, type PrivateBoardSearchProps } from '~/components/BoardSearch/PrivateBoardSearch';
import { type BoardSearchLibrary, useSingleBoardSearchState } from '~/components/BoardSearch/shared/hooks';
import { ToastLink } from '~/components/UI/ToastLink';
import { getWorkspaceAgentAssetListRowClipIds } from '~/components/v2/components/workspace/workspace-agent-asset-list/utils/toWorkspaceAgentAssetListItems';
import { CLIPS_LIST_BY_IDS_MAX_IDS } from '~/components/v2/components/workspace/workspace-agent-panel/utils/chunkWorkspaceAgentClipIds';
import { useWorkspaceAgentAssetListViewStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentAssetListViewStore';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useGoToPrivateBoardPage } from '~/hooks/useGoToPrivateBoardPage';
import { useLibraryPermissionsCache } from '~/hooks/useLibraryPermissionsCache';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { useCreateNewBoard } from '~/swr-hooks/boards/useCreateNewBoard';
import { type InitialBoardOption } from '~/types/BoardSearch';
import { containsRestrictedPathChars } from '~/utils/FileUtils';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

const BOARD_TITLE_MAX_LENGTH = 255;

export const SaveWorkspaceAgentAssetListAsBoardModalFormValidationSchema = object({
  title: string()
    .trim()
    .max(BOARD_TITLE_MAX_LENGTH, `Cannot be longer than ${BOARD_TITLE_MAX_LENGTH} characters`)
    .test(
      'restricted-char-validation',
      'Characters : and | cannot be used',
      (val) => !containsRestrictedPathChars(val || ''),
    )
    .required('Please enter a board name')
    .default(''),
});

export type SaveWorkspaceAgentAssetListAsBoardModalFormInput = InferType<
  typeof SaveWorkspaceAgentAssetListAsBoardModalFormValidationSchema
>;

type SavedBoardToastProps = {
  board: Board;
  onOpenBoard: (board: Board) => void;
  title: string;
};

const SavedBoardToast = memo(({ board, onOpenBoard, title }: SavedBoardToastProps) => {
  const handleOpen = useCallback(() => {
    onOpenBoard(board);
  }, [board, onOpenBoard]);

  return (
    <div className="flex w-full items-center justify-center overflow-hidden">
      {'Saved board '}
      <ToastLink className="flex-1 pl-1.5" data-testid="SAVED_AS_BOARD_LINK" onClick={handleOpen}>
        {title}
      </ToastLink>
    </div>
  );
});

SavedBoardToast.displayName = 'SavedBoardToast';

const fetchAllArtifactClipIds = async ({
  artifactId,
  fallbackClipIds,
  sessionId,
  workspaceId,
}: {
  artifactId: string | undefined;
  fallbackClipIds: Clip['id'][];
  sessionId: string | undefined;
  workspaceId: string | undefined;
}): Promise<Clip['id'][]> => {
  if (!artifactId || !sessionId || !workspaceId) {
    return fallbackClipIds;
  }

  const collected: Clip['id'][] = [];
  let cursor: string | undefined;

  for (;;) {
    const page = await callAirApi(() =>
      Agents.Sessions.Artifacts.Rows.list({
        agentType: 'workspace',
        artifactId,
        cursor,
        limit: CLIPS_LIST_BY_IDS_MAX_IDS,
        sessionId,
        workspaceId,
      }),
    );

    if (page.type !== 'assetList') {
      return fallbackClipIds;
    }

    collected.push(...getWorkspaceAgentAssetListRowClipIds(page.data));

    if (!page.pagination?.hasMore || !page.pagination.cursor) {
      break;
    }

    cursor = page.pagination.cursor;
  }

  return collected.length > 0 ? [...new Set(collected)] : fallbackClipIds;
};

export type SaveWorkspaceAgentAssetListAsBoardModalProps = {
  artifactId?: string;
  clipIds: Clip['id'][];
  initialLibrary?: BoardSearchLibrary;
  onClose: () => void;
  onSaved?: (params: { boardId: string; boardTitle: string }) => void;
  selectedParentBoard?: InitialBoardOption;
  sessionId?: string;
  title?: Board['title'];
  trackLocation: AirActionTrackingLocation;
  workspaceId?: string;
};

export const SaveWorkspaceAgentAssetListAsBoardModal = memo(
  ({
    artifactId,
    clipIds,
    initialLibrary,
    onClose,
    onSaved,
    selectedParentBoard,
    sessionId,
    title,
    trackLocation,
    workspaceId,
  }: SaveWorkspaceAgentAssetListAsBoardModalProps) => {
    const formId = useId();
    const titleFieldId = `${formId}-title`;
    const titleErrorId = `${formId}-title-error`;
    const locationLabelId = `${formId}-location-label`;
    const { createNewBoard } = useCreateNewBoard();
    const { goToBoardPage } = useGoToPrivateBoardPage();
    const { showToast } = useToasts();
    const currentBoard = useSelector(centralizedBoardSelector);

    const {
      formState: { errors, isSubmitting, isValid },
      handleSubmit,
      register,
    } = useForm<SaveWorkspaceAgentAssetListAsBoardModalFormInput>({
      defaultValues: { title: title || '' },
      mode: 'all',
      resolver: yupResolver(SaveWorkspaceAgentAssetListAsBoardModalFormValidationSchema),
    });

    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });

    const {
      selectedBoard: parentBoard,
      isBoardSelected,
      isLibrarySelected,
      onSelectBoard,
      onSelectLibrary,
      selectedLibrary,
      shouldShowSubBoards,
      shouldShowLibraryBoards,
    } = useSingleBoardSearchState({
      initialBoard: !initialLibrary ? selectedParentBoard || currentBoard : undefined,
      initialLibrary,
    });

    const onBoardSelectChange: PrivateBoardSearchProps['onBoardSelectChange'] = useCallback(
      ({ board, selected }) => {
        onSelectBoard(selected ? board : null);
      },
      [onSelectBoard],
    );

    const { getBoardPermissions } = useBoardPermissionsCache();
    const { getLibraryPermissions } = useLibraryPermissionsCache();

    const getIsBoardDisabled: Required<PrivateBoardSearchProps>['getIsBoardDisabled'] = useCallback(
      (board) => {
        const boardPermissions = getBoardPermissions(board.id);

        if (!canCreateBoard(boardPermissions)) {
          return {
            isDisabled: true,
            message: 'You do not have permission to create boards in this board',
          };
        }
      },
      [getBoardPermissions],
    );

    const getIsLibraryDisabled: Required<PrivateBoardSearchProps>['getIsLibraryDisabled'] = useCallback(
      (library) => {
        const libraryPermissions = getLibraryPermissions(library.id);

        if (!canCreateBoard(libraryPermissions)) {
          return {
            isDisabled: true,
            message: 'You do not have permission to create boards in this library',
          };
        }
      },
      [getLibraryPermissions],
    );

    const handleOpenSavedBoard = useCallback(
      (board: Board) => {
        useWorkspaceAgentAssetListViewStore.getState().closeArtifactView();
        goToBoardPage({ board, trackLocation: 'created-board-toast' }).catch((error) => {
          reportErrorToBugsnag({
            context: 'Failed to navigate to saved board',
            error,
            metadata: { data: { boardId: board.id } },
          });
        });
      },
      [goToBoardPage],
    );

    const onSubmit = useCallback(
      async ({ title: nextTitle }: SaveWorkspaceAgentAssetListAsBoardModalFormInput) => {
        const trimmedTitle = nextTitle.trim();
        const parentId = parentBoard?.id;
        const libraryId = selectedLibrary?.id || parentBoard?.library?.id;

        try {
          const resolvedClipIds = await fetchAllArtifactClipIds({
            artifactId,
            fallbackClipIds: clipIds,
            sessionId,
            workspaceId,
          });
          const board = await createNewBoard({
            board: {
              title: trimmedTitle,
              description: '',
              parentId: parentId !== 'root' ? parentId : undefined,
              libraryId,
            },
            clipIds: resolvedClipIds,
            trackLocation,
            shouldFollow: true,
          });

          if (!board) {
            return;
          }

          onSaved?.({ boardId: board.id, boardTitle: trimmedTitle });

          showToast(<SavedBoardToast board={board} onOpenBoard={handleOpenSavedBoard} title={trimmedTitle} />);

          onClose();
        } catch (error) {
          reportErrorToBugsnag({
            context: 'Failed to save workspace agent asset list as board',
            error,
          });
          showToast('An error occurred - please try again later');
        }
      },
      [
        artifactId,
        clipIds,
        createNewBoard,
        handleOpenSavedBoard,
        onClose,
        onSaved,
        parentBoard?.id,
        parentBoard?.library?.id,
        selectedLibrary?.id,
        sessionId,
        showToast,
        trackLocation,
        workspaceId,
      ],
    );

    if (showingSubscriptionExpiredModal) {
      return null;
    }

    return (
      <Modal data-testid="SAVE_AS_BOARD_MODAL" isOpen onDismiss={onClose}>
        <header className="mb-4 flex justify-between gap-4">
          <ModalTitle data-testid="SAVE_AS_BOARD_MODAL_TITLE">Save as board</ModalTitle>
          <ModalCloseButton className="shrink-0" onClick={onClose} />
        </header>
        <ModalDescription className="mb-4" data-testid="SAVE_AS_BOARD_MODAL_DESCRIPTION">
          Creates a new board and fills it with this static set. It is not a live search. This artifact view stays as it
          was captured.
        </ModalDescription>
        <form id={formId} onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-6">
            <Field>
              <FieldLabel htmlFor={titleFieldId}>Name</FieldLabel>
              <Input
                aria-describedby={errors.title ? titleErrorId : undefined}
                aria-invalid={Boolean(errors.title)}
                autoComplete="off"
                autoFocus
                data-testid="SAVE_AS_BOARD_MODAL_NAME_INPUT"
                hasError={Boolean(errors.title)}
                id={titleFieldId}
                size="large"
                {...register('title')}
              />
              {errors.title ? (
                <FieldText color="red" id={titleErrorId}>
                  {errors.title.message}
                </FieldText>
              ) : null}
            </Field>
          </div>

          <fieldset aria-labelledby={locationLabelId} className="mb-6">
            <Label className="mb-1.5" id={locationLabelId}>
              Location
            </Label>
            <PrivateBoardSearch
              className="[&>.workspace-boards]:h-[200px]"
              getIsBoardDisabled={getIsBoardDisabled}
              getIsLibraryDisabled={getIsLibraryDisabled}
              initialBoardId={parentBoard?.id}
              isBoardSelected={isBoardSelected}
              isLibrarySelected={isLibrarySelected}
              onBoardSelectChange={onBoardSelectChange}
              onSelectLibrary={onSelectLibrary}
              shouldShowLibraryBoards={shouldShowLibraryBoards}
              shouldShowSubBoards={shouldShowSubBoards}
            />
          </fieldset>
          <footer className="flex justify-end gap-3">
            <Button
              appearance="ghost"
              color="gray"
              data-testid="SAVE_AS_BOARD_MODAL_CANCEL"
              onClick={onClose}
              size="large"
              type="button"
            >
              Cancel
            </Button>
            <Button
              appearance="filled"
              color="blue"
              data-testid="SAVE_AS_BOARD_MODAL_SAVE"
              disabled={isSubmitting || !isValid}
              isLoading={isSubmitting}
              size="large"
              type="submit"
            >
              Save
            </Button>
          </footer>
        </form>
      </Modal>
    );
  },
);

SaveWorkspaceAgentAssetListAsBoardModal.displayName = 'SaveWorkspaceAgentAssetListAsBoardModal';
